import type {
  CustomerManagerUsageDailyResponse,
  CustomerManagerUsageOverviewResponse,
  MessageTraceDetailResponse,
  OpsUsageOverviewResponse,
  OpsUsageRequestsResponse,
  RequestLifecycleDetailResponse,
} from '@nadoutong/contracts';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { getOpsApiBaseUrl, opsApiRequest } from './client.ts';

const BASE = '/v1/ops/usage';

export type OpsUsageWindow = 'today' | '7d' | '30d' | 'custom';

export interface OpsUsageOverviewQuery {
  readonly window: OpsUsageWindow;
  readonly from?: string;
  readonly to?: string;
  readonly accountId?: string;
  readonly email?: string;
  readonly brand?: 'nadoutong' | 'nxtpath';
}

export interface OpsUsageRequestsQuery {
  readonly from?: string;
  readonly to?: string;
  readonly accountId?: string;
  readonly email?: string;
  readonly requestId?: string;
  readonly apiKeyId?: string;
  readonly apiKeyPrefix?: string;
  readonly channelId?: string;
  readonly channelSlug?: string;
  /** Epic #898 P6 (#914): exact SKU id filter. */
  readonly skuId?: string;
  /** Epic #898 P6 (#914): substring match on the canonical sku_name. */
  readonly skuName?: string;
  readonly status?: 'success' | 'error';
  readonly model?: string;
  readonly upstreamId?: string;
  readonly upstreamAccountId?: string;
  readonly upstreamKeyBucketId?: string;
  readonly upstreamChannelId?: string;
  readonly upstreamChannelSlug?: string;
  /** Egress lane filter (typed model; matches legacy egress_type values on historic rows). */
  readonly lane?: string;
  readonly routeResolutionSource?: string;
  readonly stream?: 'true' | 'false';
  readonly lifecycleStatus?: string;
  readonly brand?: 'nadoutong' | 'nxtpath';
  readonly limit: number;
  readonly offset: number;
}

function buildQuery(query: object): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query) as Array<[string, string | number | undefined]>) {
    if (value !== undefined && String(value).length > 0) {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

export async function getOpsUsageOverview(
  query: OpsUsageOverviewQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<OpsUsageOverviewResponse> {
  return opsApiRequest<OpsUsageOverviewResponse>(
    `${BASE}/overview${buildQuery(query)}`,
    { signal },
    fetchImpl,
  );
}

/**
 * Scoped CRM usage overview (epic #1035 E6-D4, #1068). `view=crm` pins
 * the projected shape for every role: sales sessions are book-scoped
 * server-side regardless, and admin sessions opt into the same
 * projection so the /crm/usage page renders one contract.
 */
export interface CrmUsageOverviewQuery {
  readonly window: OpsUsageWindow;
  readonly from?: string;
  readonly to?: string;
  readonly accountId?: string;
}

export async function getCrmUsageOverview(
  query: CrmUsageOverviewQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<CustomerManagerUsageOverviewResponse> {
  return opsApiRequest<CustomerManagerUsageOverviewResponse>(
    `${BASE}/overview${buildQuery({ ...query, view: 'crm' })}`,
    { signal },
    fetchImpl,
  );
}

export function useCrmUsageOverview(
  query: CrmUsageOverviewQuery,
): UseQueryResult<CustomerManagerUsageOverviewResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ['crm-usage', 'overview', query],
    queryFn: ({ signal }) => getCrmUsageOverview(query, fetchImpl, signal),
  });
}

/**
 * Direct download URL for the scoped CSV export — same query surface,
 * same server-side projection. Not wrapped in a hook (see reports.ts:
 * caching blobs in React Query pins them in memory); the page renders a
 * plain anchor and the browser attaches the session cookie.
 */
export function crmUsageOverviewCsvUrl(query: CrmUsageOverviewQuery): string {
  return `${getOpsApiBaseUrl()}${BASE}/overview.csv${buildQuery({ ...query, view: 'crm' })}`;
}

/**
 * Customer 360 daily series (#2155). Always requests the sales
 * projection (`view=crm`) so an admin session on this page sees the
 * same tenant-charge lens a customer manager does.
 *
 * CSV is a naked URL, not a React Query cache — same reason as
 * {@link crmUsageOverviewCsvUrl}.
 */
export interface CrmUsageDailyQuery {
  readonly window: OpsUsageWindow;
  readonly from?: string;
  readonly to?: string;
  readonly accountId: string;
  /** Required: today uses `hour`, every other window uses `day`. */
  readonly granularity: 'day' | 'hour';
}

export async function getCrmUsageDaily(
  query: CrmUsageDailyQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<CustomerManagerUsageDailyResponse> {
  return opsApiRequest<CustomerManagerUsageDailyResponse>(
    `${BASE}/daily${buildQuery({ ...query, view: 'crm' })}`,
    { signal },
    fetchImpl,
  );
}

export function useCrmUsageDaily(
  query: CrmUsageDailyQuery,
  enabled: boolean,
): UseQueryResult<CustomerManagerUsageDailyResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ['crm-usage', 'daily', query],
    queryFn: ({ signal }) => getCrmUsageDaily(query, fetchImpl, signal),
    enabled: enabled && query.accountId.length > 0,
  });
}

export function crmUsageDailyCsvUrl(query: CrmUsageDailyQuery): string {
  return `${getOpsApiBaseUrl()}${BASE}/daily.csv${buildQuery({ ...query, view: 'crm' })}`;
}

export async function getOpsUsageRequests(
  query: OpsUsageRequestsQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<OpsUsageRequestsResponse> {
  return opsApiRequest<OpsUsageRequestsResponse>(
    `${BASE}/requests${buildQuery(query)}`,
    { signal },
    fetchImpl,
  );
}

export async function getOpsMessageTrace(
  requestId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<MessageTraceDetailResponse> {
  return opsApiRequest<MessageTraceDetailResponse>(
    `/v1/ops/message-traces/${encodeURIComponent(requestId)}`,
    { signal },
    fetchImpl,
  );
}

export async function getOpsRequestLifecycle(
  requestId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<RequestLifecycleDetailResponse> {
  return opsApiRequest<RequestLifecycleDetailResponse>(
    `/v1/ops/request-lifecycle/${encodeURIComponent(requestId)}`,
    { signal },
    fetchImpl,
  );
}

export function useOpsUsageOverview(
  query: OpsUsageOverviewQuery,
): UseQueryResult<OpsUsageOverviewResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ['ops-usage', 'overview', query],
    queryFn: ({ signal }) => getOpsUsageOverview(query, fetchImpl, signal),
  });
}

export function useOpsUsageRequests(
  query: OpsUsageRequestsQuery,
): UseQueryResult<OpsUsageRequestsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ['ops-usage', 'requests', query],
    queryFn: ({ signal }) => getOpsUsageRequests(query, fetchImpl, signal),
  });
}

export function useOpsMessageTrace(
  requestId: string,
  enabled: boolean,
): UseQueryResult<MessageTraceDetailResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ['ops-message-trace', requestId],
    queryFn: ({ signal }) => getOpsMessageTrace(requestId, fetchImpl, signal),
    enabled,
  });
}

export function useOpsRequestLifecycle(
  requestId: string,
  enabled: boolean,
): UseQueryResult<RequestLifecycleDetailResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ['ops-request-lifecycle', requestId],
    queryFn: ({ signal }) => getOpsRequestLifecycle(requestId, fetchImpl, signal),
    enabled,
  });
}
